import { useState } from 'react'
import type { FormEvent } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { PASSWORD_HELP, passwordProblem } from '@/auth/password'
import { useAuth } from '@/auth/SessionProvider'
import { changePasswordAtFirstLogin, friendlyAuthError } from '@/data/auth'
import { AuthLayout, FormError } from './AuthLayout'

export function FirstLoginPage() {
  const { auth, refresh, signOut } = useAuth()
  const navigate = useNavigate()
  const profile = auth.status === 'signed-in' ? auth.profile : null

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [show, setShow] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (busy) return
    const problem = passwordProblem(password, { email: profile?.email, fullName: profile?.fullName })
    if (problem) return setError(problem)
    if (password !== confirm) return setError('The two passwords do not match.')

    setBusy(true)
    setError(null)
    try {
      await changePasswordAtFirstLogin(password)
      await refresh()
      // The router now sees must_change_password = false and lets them in.
      await navigate({ to: '/' })
    } catch (err) {
      setError(friendlyAuthError(err))
      setBusy(false)
    }
  }

  return (
    <AuthLayout
      title="Choose your own password"
      subtitle={`Welcome${profile ? `, ${profile.fullName.split(' ')[0]}` : ''}. You signed in with a temporary password. Pick one only you know to continue.`}
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        {error && <FormError>{error}</FormError>}
        <Input
          label="New password"
          type={show ? 'text' : 'password'}
          name="new-password"
          autoComplete="new-password"
          hint={PASSWORD_HELP}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={busy}
          autoFocus
        />
        <Input
          label="Type it again"
          type={show ? 'text' : 'password'}
          name="confirm-password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          disabled={busy}
        />
        <label className="flex items-center gap-2 text-body text-muted min-h-[44px] select-none">
          <input
            type="checkbox"
            className="size-5 rounded border-line-strong"
            checked={show}
            onChange={(e) => setShow(e.target.checked)}
          />
          Show passwords
        </label>
        <Button type="submit" size="lg" isLoading={busy} className="w-full">
          {busy ? 'Saving…' : 'Save and continue'}
        </Button>
        <Button type="button" variant="ghost" onClick={() => void signOut()} disabled={busy} className="w-full">
          Sign out
        </Button>
      </form>
    </AuthLayout>
  )
}
